import { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import {
    Box,
    Button,
    FormControl,
    Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { formatDate } from "../../../../utils/DateTime";
import { DateFormats } from "../../../../constants/Date";

import { GridBody, OuterGrid, HeaderOuterGrid, ButtonGrid, PatientDetailBox, HeaderStyling, HeaderModuleName, ButtonWidth } from "../../styles/styles";
import fontWeight from "../../../../styles/mui/fontWeight";
import { colors } from "../../../../styles/colors";
import { FormInputText } from "../../../../components/formComponents/FormInputText";
import DatePicker from "../../../../components/formComponents/DatePicker";
import SearchRecords from "../../containers/SearchRecords";
import { displayFlex, flexAllCentre } from "../../../../styles/mui/styles/display";
import { AllPatientRecordResponse } from "../../../../models/Api/AllPatientRecord";
import { getValue } from "../../../../utils";
import { PatientRecordSearch  } from "../../../../models/Patient";
import { HeaderTitles, PatientRecordDispatchTypes, SearchPayloadRecord } from "../../../../constants/AllPatientRecords";
import { rules } from "../../../../utils/validation/Validation";
import { SearchPayloadForRecords } from "../../../../models/Authorization";

export interface PropsFromState {
    allPatientRecord: AllPatientRecordResponse;
}
export interface PropsFromDispatch {
    postAllPatientRecord: (payload?: SearchPayloadForRecords) => void;
    resetStates: (actionType: string[]) => void;
}

type Props = PropsFromState & PropsFromDispatch;
const CheckEligibility: React.FC<Props> = ({
    postAllPatientRecord,
    allPatientRecord,
    resetStates
}: Props) => {

    const { response , loading } = allPatientRecord;
    const inputRef = useRef<HTMLInputElement>(null);
    const [patientRecord, setPatientRecord] =
        useState<PatientRecordSearch | null>(null);

    const [searchPayload, setSearchPayload] = useState<SearchPayloadForRecords>(SearchPayloadRecord);
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        if (!loading) {
          setPatientRecord(response);
        }
      }, [response]);
    
      useEffect(() => {
        return () => {
          resetStates([PatientRecordDispatchTypes.POST_PATIENT_RECORD_RESET]);
        };
      }, []);

    const methods = useForm<SearchPayloadForRecords>({
        defaultValues: {
            lastName: "",
            dateOfBirth: null,
            subscriberId: "",
        },
        mode: "all",
    });

    const { control, handleSubmit, setError, reset, formState: { isDirty } } = methods;

    const validateSearchCriteria = (data: SearchPayloadForRecords) => {
        const lastNameFilled = !!data.lastName?.trim();
        const dateOfBirthFilled = !!data.dateOfBirth;
        const subscriberIDFilled = !!data.subscriberId?.trim();
      
        const filledFieldsCount = [
          lastNameFilled,
          dateOfBirthFilled,
          subscriberIDFilled,
        ].filter(Boolean).length;
      
        if (filledFieldsCount < 2) {
          if (!lastNameFilled) {
            setError("lastName", {
              type: "manual",
              message: "Last Name is required for search",
            });
          }
      
          if (!dateOfBirthFilled) {
            setError("dateOfBirth", {
              type: "manual",
              message: "Date of birth is required for search",
            });
          }
      
          if (!subscriberIDFilled) {
            setError("subscriberId", {
              type: "manual",
              message: "SubscriberId is required for search",
            });
          }
      
          return false;
        }
      
        return true;
      };

    const onSearch = (data: SearchPayloadForRecords) => {
        const payload = { ...data, pageNo: 1, pageSize: 10 };
        const isValid = validateSearchCriteria(data);

        if (!isValid) return;

        setHasSearched(true);

        setSearchPayload({
            lastName: data.lastName,
            subscriberId: data.subscriberId,
            dateOfBirth: data.dateOfBirth ? formatDate(data.dateOfBirth, DateFormats.YYYY_MM_DD) : null,
        });

        postAllPatientRecord(payload);
    };

    const handleReset = () => {
        reset({
            lastName: "",
            dateOfBirth: null,
            subscriberId: "",
        });
        setPatientRecord(null);
        setHasSearched(false);
        inputRef.current?.focus();
    }

    const hasRecords =
    !!patientRecord?.patientResponse?.length &&
    patientRecord.patientResponse[0].patientId !== 0;

    return (
        <Box sx={OuterGrid}>
            <Grid container>
                <Grid size={12}>
                    <Grid container sx={GridBody}>
                        <Grid size={12}>
                            <Grid container sx={HeaderOuterGrid}>
                                <Grid size={12} sx={flexAllCentre}>
                                    <Typography
                                        variant="h1"
                                        fontWeight={fontWeight.Weight[6]}
                                        color={colors.black[2]}
                                        fontSize={"1.32rem"}
                                    >
                                        {HeaderTitles.WELCOME_TO_THE_HEALTH_PLAN_ACCESS_PORTAL}
                                    </Typography>
                                </Grid>
                                <Grid size={12} sx={flexAllCentre} pt={"1.3rem"}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={fontWeight.Weight[2]}
                                        color={colors.black[2]}
                                    >
                                        {HeaderTitles.TANGO_SECURE_PORTAL_TITLE}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={12} pt={"1.2rem"} pl={"0.5rem"}>
                            <Grid container>
                                <Grid mr={"0.5rem"}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={fontWeight.Weight[7]}
                                        color={colors.black[2]}
                                    >
                                        Member Search:
                                    </Typography>
                                </Grid>
                                <Grid size={9}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={fontWeight.Weight[7]}
                                        color={colors.black[2]}
                                    >
                                        {HeaderTitles.SEARCH_TITLE}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={12} pt={"0.7rem"} pl={"0.5rem"}>
                            <Grid container>
                                <Grid size={3} pr={"1.4rem"}>
                                    <FormInputText
                                        name="lastName"
                                        label="Last Name"
                                        control={control}
                                        helper={rules.patientLastName}
                                        searchInputRef={inputRef}
                                    />
                                </Grid>
                                <Grid size={3} pl={"1.4rem"} pr={"1.4rem"}>
                                    <FormControl variant="standard" fullWidth>
                                        <DatePicker
                                            name="dateOfBirth"
                                            control={control}
                                            label={"Date of Birth"}
                                            allowFutureDate={false}
                                            InputStyle={{ width: "100%" }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={3} pl={"1.4rem"} pr={"1rem"}>
                                    <FormInputText
                                        name="subscriberId"
                                        label="Subscriber ID"
                                        control={control}
                                        helper={rules.subscriberId}
                                    />
                                </Grid>
                                <Grid size={3} sx={ButtonGrid}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit(onSearch)}
                                        sx={ButtonWidth}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleReset}
                                        sx={ButtonWidth}
                                        disabled={!isDirty}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {hasSearched && patientRecord && !loading && (
                        <PatientDetailBox>
                            {hasRecords &&  (
                                <Grid container sx={HeaderStyling}>
                                    <Grid size={2.3} sx={displayFlex}>
                                        <Box>
                                            <Typography
                                                variant="h4"
                                                fontWeight={fontWeight.Weight[6]}
                                                sx={HeaderModuleName}
                                                color={colors.black[3]}
                                            >
                                                RECORDS:
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={fontWeight.Weight[2]}
                                                color={colors.black[2]}
                                            >
                                                Search Results ({getValue(patientRecord, "patientResponse.length", 0)})
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                            <SearchRecords patientRecord={patientRecord} searchPayload={searchPayload} />
                        </PatientDetailBox>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default CheckEligibility;